import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { token, type SpaceStep } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Stack.module.css';

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'column';
  /** A step from the space scale. There is no arbitrary gap. */
  gap?: SpaceStep;
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  wrap?: boolean;
  inline?: boolean;
  /** Push children apart along the axis without a wrapper. */
  grow?: boolean;
  children: ReactNode;
}

const ALIGN = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
} as const;

const JUSTIFY = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
} as const;

/**
 * The layout escape hatch that cannot leak an off-scale value: gaps are token
 * steps and alignment is an enum, so a surface never writes a raw length.
 */
export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  {
    direction = 'column',
    gap = 'sp05',
    align = 'stretch',
    justify = 'start',
    wrap = false,
    inline = false,
    grow = false,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const vars: StyleWithVars = {
    '--dpem-stack-gap': token.space[gap],
    '--dpem-stack-align': ALIGN[align],
    '--dpem-stack-justify': JUSTIFY[justify],
  };

  return (
    <div
      {...rest}
      ref={ref}
      className={clsx(
        styles.stack,
        direction === 'row' ? styles.row : styles.column,
        inline && styles.inline,
        wrap && styles.wrap,
        grow && styles.grow,
        className,
      )}
      style={mergeStyle(vars, style)}
    >
      {children}
    </div>
  );
});
